import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft,
  Scale,
  Users,
  Vote,
  Shield,
  FileText,
  AlertCircle,
  CheckCircle,
  Gavel,
  Building2,
  Heart,
  Globe
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const BylawsPage = () => {
  usePageTitle('Club Bylaws');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link 
            to="/about" 
            className="inline-flex items-center text-sm text-slate-400 hover:text-white mb-6"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to About
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
              <Scale className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold">Club Bylaws</h1>
              <p className="text-slate-400">All-American Dog Club Governing Document</p>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Badge className="bg-green-600">Effective: March 2026</Badge>
            <Badge variant="outline" className="text-slate-300 border-slate-600">Version 1.0</Badge>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Preamble */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Preamble
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <p>
              The All-American Dog Club (AADC) is established as a community-driven organization 
              dedicated to celebrating all dogs and their human companions, regardless of breed, 
              pedigree, or documentation status. We believe every dog deserves recognition, every 
              owner deserves community, and every voice deserves to be heard.
            </p>
            <p>
              These bylaws establish the governance framework for AADC, ensuring transparent 
              operations, member rights, and fair dispute resolution.
            </p>
          </CardContent>
        </Card>

        {/* Article I - Name and Purpose */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Article I: Name and Purpose
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <h4>Section 1. Name</h4>
            <p>This organization shall be known as the All-American Dog Club (AADC).</p>
            
            <h4>Section 2. Purpose</h4>
            <p>The purposes of AADC are:</p>
            <ul>
              <li>To provide a registry and community platform for all dogs, including purebreds, mixed breeds, rescues, and undocumented dogs</li>
              <li>To promote responsible dog ownership and welfare</li>
              <li>To connect dog owners through events, forums, and local communities</li>
              <li>To advance canine health through education and research collaboration</li>
              <li>To support rescue organizations and adoption efforts</li>
            </ul>

            <h4>Section 3. Non-Profit Intent</h4>
            <p>
              AADC operates as a community-benefit organization. While sustainable operations may 
              require membership fees and partnerships, the primary mission is service to members 
              and the broader pet community, not profit maximization.
            </p>
          </CardContent>
        </Card>

        {/* Article II - Membership */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Article II: Membership
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <h4>Section 1. Eligibility</h4>
            <p>
              Membership is open to any individual who supports the purposes of AADC and agrees 
              to abide by these bylaws and club policies.
            </p>

            <h4>Section 2. Membership Tiers</h4>
            <ul>
              <li><strong>Free Members:</strong> Basic access to community features</li>
              <li><strong>Charter Members:</strong> Early supporters with enhanced benefits and voting rights on club matters</li>
              <li><strong>Premium Members:</strong> Full access to all features including health records, matchmaking, and ad-free experience</li>
              <li><strong>Beta Members:</strong> Early adopters helping shape the platform</li>
            </ul>

            <h4>Section 3. Rights of Members</h4>
            <p>All members in good standing have the right to:</p>
            <ul>
              <li>Participate in community forums and discussions</li>
              <li>Register their pets in the club database</li>
              <li>Access member resources and benefits per their tier</li>
              <li>Submit feedback and suggestions</li>
              <li>Request ombudsman services for dispute resolution</li>
              <li>Vote on club matters (Charter and Premium members)</li>
            </ul>

            <h4>Section 4. Responsibilities</h4>
            <p>Members agree to:</p>
            <ul>
              <li>Treat fellow members with respect</li>
              <li>Provide accurate information about themselves and their pets</li>
              <li>Follow community guidelines and code of conduct</li>
              <li>Report concerns through proper channels</li>
            </ul>
          </CardContent>
        </Card>

        {/* Article III - Governance */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Vote className="w-5 h-5 text-purple-500" />
              Article III: Governance
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <h4>Section 1. Leadership Structure</h4>
            <p>AADC governance consists of:</p>
            <ul>
              <li><strong>Founding Team:</strong> Responsible for platform operations and strategic direction</li>
              <li><strong>Club Officers:</strong> Volunteer leaders managing specific areas (events, forums, etc.)</li>
              <li><strong>Regional Leaders:</strong> Representatives for geographic areas</li>
              <li><strong>Advisory Council:</strong> Elected or appointed advisors from the membership</li>
            </ul>

            <h4>Section 2. Member Voting</h4>
            <p>
              Charter and Premium members may vote on significant club matters including:
            </p>
            <ul>
              <li>Major policy changes affecting member rights</li>
              <li>Community guidelines updates</li>
              <li>Selection of Advisory Council members</li>
              <li>Other matters presented to the membership</li>
            </ul>
            <p>
              Voting shall be conducted electronically through the club platform. Each eligible 
              member receives one vote. Decisions require a simple majority unless otherwise 
              specified.
            </p>

            <h4>Section 3. Transparency</h4>
            <p>
              The leadership shall maintain transparency by sharing roadmaps, financial summaries, 
              and significant decisions with the membership through appropriate channels.
            </p>
          </CardContent>
        </Card>

        {/* Article IV - Ombudsman */}
        <Card className="mb-8 border-2 border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-amber-600" />
              Article IV: Ombudsman & Dispute Resolution
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 mb-4">
              <p className="text-amber-800 font-medium flex items-center gap-2 mb-0">
                <Shield className="w-5 h-5" />
                This article establishes AADC's commitment to fair and impartial dispute resolution.
              </p>
            </div>

            <h4>Section 1. Ombudsman Role</h4>
            <p>
              AADC provides ombudsman services to help resolve disputes between members, or 
              between members and the organization. The ombudsman serves as a neutral, 
              confidential resource for conflict resolution.
            </p>

            <h4>Section 2. Independence & Immunity</h4>
            <p>The ombudsman function operates with:</p>
            <ul>
              <li><strong>Independence:</strong> The ombudsman acts independently from club management and is not subject to direction regarding case outcomes</li>
              <li><strong>Immunity:</strong> The ombudsman shall not be held liable for good-faith actions taken in the course of dispute resolution</li>
              <li><strong>Confidentiality:</strong> Communications with the ombudsman are confidential except where disclosure is required by law or to prevent imminent harm</li>
            </ul>

            <h4>Section 3. Scope of Services</h4>
            <p>The ombudsman may assist with:</p>
            <ul>
              <li>Member-to-member disputes</li>
              <li>Complaints about club policies or decisions</li>
              <li>Concerns about moderator actions</li>
              <li>Marketplace transaction disputes</li>
              <li>General grievances requiring neutral mediation</li>
            </ul>

            <h4>Section 4. Process</h4>
            <ol>
              <li><strong>Request:</strong> Members may request ombudsman services through the contact form or designated channel</li>
              <li><strong>Review:</strong> The ombudsman reviews the matter and determines appropriate action</li>
              <li><strong>Mediation:</strong> If appropriate, the ombudsman facilitates discussion between parties</li>
              <li><strong>Recommendation:</strong> The ombudsman may provide non-binding recommendations</li>
              <li><strong>Resolution:</strong> Parties are encouraged but not required to accept recommendations</li>
            </ol>

            <h4>Section 5. Limitations</h4>
            <p>
              The ombudsman does not have authority to make binding decisions, reverse management 
              actions, or award damages. The ombudsman service is provided in good faith but 
              AADC cannot guarantee specific outcomes or be held liable for the results of 
              ombudsman proceedings.
            </p>

            <h4>Section 6. Alternative Remedies</h4>
            <p>
              Ombudsman services do not replace legal remedies. Members retain all legal rights 
              and may pursue other avenues as appropriate.
            </p>
          </CardContent>
        </Card>

        {/* Article V - Conduct */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-500" />
              Article V: Code of Conduct & Enforcement
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <h4>Section 1. Expected Conduct</h4>
            <p>Members shall:</p>
            <ul>
              <li>Treat others with respect and courtesy</li>
              <li>Refrain from harassment, discrimination, or abuse</li>
              <li>Not engage in fraud or misrepresentation</li>
              <li>Respect intellectual property and privacy</li>
              <li>Comply with applicable laws</li>
            </ul>

            <h4>Section 2. Enforcement</h4>
            <p>Violations may result in:</p>
            <ul>
              <li>Warning</li>
              <li>Temporary suspension of privileges</li>
              <li>Permanent ban from the platform</li>
            </ul>
            <p>
              Members may appeal enforcement actions through the ombudsman process.
            </p>
          </CardContent>
        </Card>

        {/* Article VI - Amendments */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-500" />
              Article VI: Amendments
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <h4>Section 1. Proposal</h4>
            <p>
              Amendments to these bylaws may be proposed by the leadership or by petition of 
              at least 10% of voting members.
            </p>

            <h4>Section 2. Notice</h4>
            <p>
              Proposed amendments shall be published to members at least 30 days before any vote.
            </p>

            <h4>Section 3. Approval</h4>
            <p>
              Amendments require approval by two-thirds (2/3) of votes cast.
            </p>
          </CardContent>
        </Card>

        {/* Adoption Notice */}
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-green-600 shrink-0" />
              <div>
                <h3 className="font-semibold text-green-800 mb-2">Adoption & Effective Date</h3>
                <p className="text-green-700">
                  These bylaws were adopted by the founding team of the All-American Dog Club 
                  and became effective in March 2026. They represent our commitment to transparent, 
                  member-focused governance.
                </p>
                <p className="text-sm text-green-600 mt-3">
                  Questions about these bylaws may be directed to the ombudsman or club leadership 
                  through the <Link to="/contact" className="underline">contact form</Link>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BylawsPage;
